/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/** Loads Building Statistics once for the table and chart on a System Statistics page. */

import { useEffect, useState } from 'react';

/* Import API functions */
import { eeCheckApi } from '@/api/eeCheckApi';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';

/* Import libraries */
import { isExpiredSessionError } from '@/lib/app/debugRequests';

/* Import types */
import type { BuildingStatisticsData } from '@/types/eeCheck';

function getBuildingStatisticsSystemKey(systemKey: string): string {
    return systemKey === 'GN1' ? 'GN.Building' : systemKey;
}

export function useBuildingStatistics(systemKey: string | undefined) {
    const { t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const [statistics, setStatistics] = useState<BuildingStatisticsData | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isSessionExpired || !accessToken || !systemKey) {
            setStatistics(null);
            setErrorMessage('');
            setIsLoading(false);
            return;
        }

        const requestedSystemKey = systemKey;
        let isCancelled = false;

        async function loadStatistics(): Promise<void> {
            setIsLoading(true);
            setErrorMessage('');
            setStatistics(null);

            try {
                const { data } = await eeCheckApi.requestBuildingStatistics(
                    accessToken,
                    getBuildingStatisticsSystemKey(requestedSystemKey),
                );

                if (isCancelled) {
                    return;
                }

                if (!data.success || data.error || !data.data) {
                    throw new Error(data.message ?? t('Building statistics could not be loaded.'));
                }

                setStatistics(data.data);
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                if (isExpiredSessionError(error)) {
                    onSessionExpired();
                    return;
                }

                setStatistics(null);
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : t('Building statistics could not be loaded.'),
                );
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        }

        void loadStatistics();

        return () => {
            isCancelled = true;
        };
    }, [accessToken, isSessionExpired, onSessionExpired, systemKey, t]);

    return { errorMessage, isLoading, statistics };
}
